import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { sessions, clients } from '../../api/client'
import PageHeader from '../../components/PageHeader'
import { Modal } from '../../components/ui/Modal'
import { Button, FloatingButton, IconButton } from '../../components/ui/Button'
import { StatusBadge } from '../../components/ui/Badge'
import { Select, TextInput, TextArea } from '../../components/ui/FormInput'
import { PageLayout } from '../../components/ui/PageLayout'
import { Icon } from '../../components/ui/Icon'

// Session type labels
const sessionTypeLabels = {
  training: 'Training',
  assessment: 'Assessment',
  consultation: 'Consultation',
  weigh_in: 'Weigh-In',
  other: 'Other',
}

// Recurrence rule labels
const recurrenceLabels = {
  weekly: 'Weekly',
  biweekly: 'Every 2 weeks',
  monthly: 'Monthly',
}

// Helper to format time
function formatTime(dateStr) {
  if (!dateStr) return ''
  const date = new Date(dateStr)
  return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
}

// Helper to format date
function formatDate(date) {
  return date.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })
}

// Get start of week (Sunday)
function getStartOfWeek(date) {
  const d = new Date(date)
  const day = d.getDay()
  d.setDate(d.getDate() - day)
  d.setHours(0, 0, 0, 0)
  return d
}

// Get end of week (Saturday)
function getEndOfWeek(date) {
  const d = getStartOfWeek(date)
  d.setDate(d.getDate() + 7)
  return d
}

// Session card component
function SessionCard({ session, onSelect }) {
  const clientName = session.client
    ? `${session.client.firstName} ${session.client.lastName}`
    : 'Unknown Client'

  return (
    <button
      onClick={() => onSelect(session)}
      className="w-full text-left bg-white dark:bg-dial-surface rounded-lg border border-gray-200 dark:border-dial-border p-3 hover:border-primary-300 dark:hover:border-lume-teal/50 hover:shadow-sm transition-all"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="font-medium text-gray-900 dark:text-indices-bright truncate">
            {clientName}
            {session.recurrenceGroupId && (
              <Icon name="refresh" className="w-3.5 h-3.5 inline ml-1.5 text-primary-500 dark:text-lume-teal/70" />
            )}
          </p>
          <p className="text-sm text-gray-500 dark:text-indices-muted mt-0.5">
            {formatTime(session.scheduledAt)} · {session.durationMinutes}min
          </p>
          {session.title && (
            <p className="text-xs text-gray-500 dark:text-indices-muted mt-1 truncate">{session.title}</p>
          )}
        </div>
        <StatusBadge status={session.status} />
      </div>
    </button>
  )
}

// New session modal
function NewSessionModal({ isOpen, onClose, onSave, clientsList }) {
  const [formData, setFormData] = useState({
    clientId: '',
    scheduledAt: '',
    durationMinutes: 60,
    sessionType: 'training',
    title: '',
    location: '',
    notes: '',
    recurrenceRule: '',
  })

  if (!isOpen) return null

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!formData.clientId || !formData.scheduledAt) {
      alert('Please select a client and date/time')
      return
    }
    onSave(formData)
  }

  const clientOptions = clientsList.map((client) => ({
    value: client.id,
    label: `${client.firstName} ${client.lastName}`,
  }))

  const durationOptions = [
    { value: 30, label: '30 min' },
    { value: 45, label: '45 min' },
    { value: 60, label: '60 min' },
    { value: 90, label: '90 min' },
    { value: 120, label: '120 min' },
  ]

  const typeOptions = Object.entries(sessionTypeLabels).map(([value, label]) => ({
    value,
    label,
  }))

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="New Session">
      <form onSubmit={handleSubmit} className="space-y-4">
        <Select
          label="Client"
          required
          value={formData.clientId}
          onChange={(e) => setFormData({ ...formData, clientId: e.target.value })}
          options={clientOptions}
          placeholder="Select a client..."
        />

        <TextInput
          label="Date & Time"
          required
          type="datetime-local"
          value={formData.scheduledAt}
          onChange={(e) => setFormData({ ...formData, scheduledAt: e.target.value })}
        />

        <div className="grid grid-cols-2 gap-4">
          <Select
            label="Duration"
            value={formData.durationMinutes}
            onChange={(e) => setFormData({ ...formData, durationMinutes: parseInt(e.target.value) })}
            options={durationOptions}
          />

          <Select
            label="Type"
            value={formData.sessionType}
            onChange={(e) => setFormData({ ...formData, sessionType: e.target.value })}
            options={typeOptions}
          />
        </div>

        <div>
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-indices-medium cursor-pointer">
            <input
              type="checkbox"
              checked={formData.recurrenceRule !== ''}
              onChange={(e) => setFormData({ ...formData, recurrenceRule: e.target.checked ? 'weekly' : '' })}
              className="rounded border-gray-300 dark:border-dial-border text-primary-600 dark:text-lume-teal focus:ring-primary-500"
            />
            Recurring
          </label>
          {formData.recurrenceRule !== '' && (
            <Select
              label=""
              value={formData.recurrenceRule}
              onChange={(e) => setFormData({ ...formData, recurrenceRule: e.target.value })}
              options={[
                { value: 'weekly', label: 'Weekly (26 sessions)' },
                { value: 'biweekly', label: 'Every 2 weeks (13 sessions)' },
                { value: 'monthly', label: 'Monthly (6 sessions)' },
              ]}
              className="mt-2"
            />
          )}
        </div>

        <TextInput
          label="Title (optional)"
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          placeholder="e.g., Leg Day, Initial Assessment"
        />

        <TextInput
          label="Location (optional)"
          value={formData.location}
          onChange={(e) => setFormData({ ...formData, location: e.target.value })}
          placeholder="e.g., Main Gym, Studio B"
        />

        <TextArea
          label="Notes (optional)"
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          rows={2}
          placeholder="Notes visible to client..."
        />

        <div className="flex gap-3 pt-2">
          <Button variant="secondary" type="button" onClick={onClose} fullWidth>
            Cancel
          </Button>
          <Button variant="primary" type="submit" fullWidth>
            Create Session
          </Button>
        </div>
      </form>
    </Modal>
  )
}

// Session detail modal
function SessionDetailModal({ session, onClose, onUpdate, onCancel, onComplete, onNoShow, onCancelSeries }) {
  if (!session) return null

  const clientName = session.client
    ? `${session.client.firstName} ${session.client.lastName}`
    : 'Unknown Client'

  const canModify = session.status === 'scheduled' || session.status === 'confirmed'

  return (
    <Modal isOpen={!!session} onClose={onClose} title="Session Details">
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-primary-100 dark:bg-dial-elevated flex items-center justify-center">
            <Icon name="user" className="w-5 h-5 text-primary-600 dark:text-lume-teal" />
          </div>
          <div>
            <p className="font-medium text-gray-900 dark:text-indices-bright">{clientName}</p>
            <p className="text-sm text-gray-500 dark:text-indices-muted">{session.client?.email}</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 pt-2">
          <div>
            <p className="text-xs text-gray-500 dark:text-indices-muted uppercase tracking-wide">Date & Time</p>
            <p className="font-medium text-gray-900 dark:text-indices-bright">
              {new Date(session.scheduledAt).toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })}
            </p>
            <p className="text-gray-600 dark:text-indices-muted">{formatTime(session.scheduledAt)}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 dark:text-indices-muted uppercase tracking-wide">Duration</p>
            <p className="font-medium text-gray-900 dark:text-indices-bright">{session.durationMinutes} min</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs text-gray-500 dark:text-indices-muted uppercase tracking-wide">Type</p>
            <p className="font-medium text-gray-900 dark:text-indices-bright">{sessionTypeLabels[session.sessionType] || session.sessionType}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 dark:text-indices-muted uppercase tracking-wide">Status</p>
            <StatusBadge status={session.status} />
          </div>
        </div>

        {session.title && (
          <div>
            <p className="text-xs text-gray-500 dark:text-indices-muted uppercase tracking-wide">Title</p>
            <p className="text-gray-900 dark:text-indices-bright">{session.title}</p>
          </div>
        )}

        {session.location && (
          <div>
            <p className="text-xs text-gray-500 dark:text-indices-muted uppercase tracking-wide">Location</p>
            <p className="text-gray-900 dark:text-indices-bright">{session.location}</p>
          </div>
        )}

        {session.notes && (
          <div>
            <p className="text-xs text-gray-500 dark:text-indices-muted uppercase tracking-wide">Notes (shared with client)</p>
            <p className="text-gray-900 dark:text-indices-bright">{session.notes}</p>
          </div>
        )}

        {session.trainerNotes && (
          <div>
            <p className="text-xs text-gray-500 dark:text-indices-muted uppercase tracking-wide">Trainer Notes (private)</p>
            <p className="text-gray-900 dark:text-indices-bright">{session.trainerNotes}</p>
          </div>
        )}

        {session.recurrenceGroupId && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-primary-50 dark:bg-dial-elevated border border-primary-200 dark:border-dial-border">
            <Icon name="refresh" className="w-4 h-4 text-primary-600 dark:text-lume-teal" />
            <span className="text-sm text-primary-700 dark:text-lume-teal">
              Recurring · {recurrenceLabels[session.recurrenceRule] || session.recurrenceRule}
            </span>
          </div>
        )}

        {canModify && (
          <div className="flex flex-col gap-2 pt-4 border-t border-gray-200 dark:border-dial-border">
            <Button variant="success" fullWidth onClick={() => onComplete(session)}>
              Mark Completed
            </Button>
            <div className="flex gap-2">
              <Button variant="warning" fullWidth onClick={() => onNoShow(session)}>
                No Show
              </Button>
              <Button variant="danger" fullWidth onClick={() => onCancel(session)}>
                Cancel
              </Button>
            </div>
            {session.recurrenceGroupId && (
              <Button variant="danger" fullWidth onClick={() => onCancelSeries(session)}>
                Cancel Entire Series
              </Button>
            )}
          </div>
        )}

        {session.status === 'cancelled' && session.cancelledReason && (
          <div className="pt-4 border-t border-gray-200 dark:border-dial-border">
            <p className="text-xs text-gray-500 dark:text-indices-muted uppercase tracking-wide">Cancellation Reason</p>
            <p className="text-gray-900 dark:text-indices-bright">{session.cancelledReason}</p>
          </div>
        )}
      </div>
    </Modal>
  )
}

export default function SchedulePage() {
  const queryClient = useQueryClient()
  const [currentWeekStart, setCurrentWeekStart] = useState(() => getStartOfWeek(new Date()))
  const [showNewModal, setShowNewModal] = useState(false)
  const [selectedSession, setSelectedSession] = useState(null)

  // Calculate week range
  const weekEnd = getEndOfWeek(currentWeekStart)
  const weekDays = useMemo(() => {
    const days = []
    const d = new Date(currentWeekStart)
    for (let i = 0; i < 7; i++) {
      days.push(new Date(d))
      d.setDate(d.getDate() + 1)
    }
    return days
  }, [currentWeekStart])

  // Fetch sessions for current week
  const { data: sessionsData, isLoading } = useQuery({
    queryKey: ['sessions', currentWeekStart.toISOString()],
    queryFn: () => sessions.list({
      start: currentWeekStart.toISOString(),
      end: weekEnd.toISOString(),
    }),
  })

  // Fetch clients for new session modal
  const { data: clientsData } = useQuery({
    queryKey: ['clients'],
    queryFn: clients.list,
  })

  const sessionsList = sessionsData || []
  const clientsList = clientsData || []

  // Group sessions by day
  const sessionsByDay = useMemo(() => {
    const grouped = {}
    weekDays.forEach(day => {
      const key = day.toDateString()
      grouped[key] = sessionsList.filter(session => {
        const sessionDate = new Date(session.scheduledAt)
        return sessionDate.toDateString() === key
      }).sort((a, b) => new Date(a.scheduledAt) - new Date(b.scheduledAt))
    })
    return grouped
  }, [sessionsList, weekDays])

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data) => {
      const body = {
        scheduledAt: new Date(data.scheduledAt).toISOString().replace(/\.\d{3}Z$/, 'Z'),
        durationMinutes: data.durationMinutes,
        sessionType: data.sessionType,
        title: data.title,
        location: data.location,
        notes: data.notes,
      }
      if (data.recurrenceRule) {
        body.recurrenceRule = data.recurrenceRule
      }
      return sessions.create(data.clientId, body)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sessions'] })
      setShowNewModal(false)
    },
    onError: (error) => {
      alert('Failed to create session: ' + (error.message || 'Unknown error'))
    },
  })

  const cancelMutation = useMutation({
    mutationFn: ({ id, reason }) => sessions.cancel(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sessions'] })
      setSelectedSession(null)
    },
  })

  const completeMutation = useMutation({
    mutationFn: ({ id, notes }) => sessions.complete(id, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sessions'] })
      setSelectedSession(null)
    },
  })

  const noShowMutation = useMutation({
    mutationFn: ({ id }) => sessions.noShow(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sessions'] })
      setSelectedSession(null)
    },
  })

  const cancelSeriesMutation = useMutation({
    mutationFn: ({ id }) => sessions.cancelSeries(id),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['sessions'] })
      setSelectedSession(null)
      alert(`${data.cancelled} future sessions cancelled.`)
    },
  })

  // Navigation
  const goToPreviousWeek = () => {
    const newStart = new Date(currentWeekStart)
    newStart.setDate(newStart.getDate() - 7)
    setCurrentWeekStart(newStart)
  }

  const goToNextWeek = () => {
    const newStart = new Date(currentWeekStart)
    newStart.setDate(newStart.getDate() + 7)
    setCurrentWeekStart(newStart)
  }

  const goToToday = () => {
    setCurrentWeekStart(getStartOfWeek(new Date()))
  }

  // Check if day is today
  const isToday = (date) => {
    return date.toDateString() === new Date().toDateString()
  }

  return (
    <PageLayout>
      <PageHeader title="Schedule" showBack backTo="/" />

      {/* Week Navigation */}
      <div className="bg-white dark:bg-dial-surface border-b border-gray-200 dark:border-dial-border px-4 py-3 flex items-center justify-between sticky top-0 z-10">
        <IconButton variant="ghost" onClick={goToPreviousWeek}>
          <Icon name="chevronLeft" className="w-5 h-5" />
        </IconButton>

        <div className="text-center">
          <p className="font-medium text-gray-900 dark:text-indices-bright">
            {currentWeekStart.toLocaleDateString([], { month: 'long', year: 'numeric' })}
          </p>
          <p className="text-sm text-gray-500 dark:text-indices-muted">
            {formatDate(currentWeekStart)} - {formatDate(new Date(weekEnd.getTime() - 1))}
          </p>
        </div>

        <IconButton variant="ghost" onClick={goToNextWeek}>
          <Icon name="chevronRight" className="w-5 h-5" />
        </IconButton>
      </div>

      {/* Today button */}
      <div className="px-4 py-2 flex justify-center">
        <Button variant="ghost" size="sm" onClick={goToToday} className="text-primary-600 dark:text-lume-teal rounded-full">
          Today
        </Button>
      </div>

      {/* Calendar Grid */}
      <div className="px-4 pb-24">
        {isLoading ? (
          <div className="text-center py-8 text-gray-500 dark:text-indices-muted">Loading...</div>
        ) : (
          <div className="space-y-4">
            {weekDays.map(day => {
              const dayKey = day.toDateString()
              const daySessions = sessionsByDay[dayKey] || []

              return (
                <div key={dayKey}>
                  <div className={`flex items-center gap-2 mb-2 ${isToday(day) ? 'text-primary-600 dark:text-lume-teal' : 'text-gray-500 dark:text-indices-muted'}`}>
                    <span className={`text-sm font-medium ${isToday(day) ? 'bg-primary-600 dark:bg-lume-teal text-white dark:text-dial-black px-2 py-0.5 rounded' : ''}`}>
                      {day.toLocaleDateString([], { weekday: 'short' })}
                    </span>
                    <span className="text-sm">
                      {day.toLocaleDateString([], { month: 'short', day: 'numeric' })}
                    </span>
                    <span className="text-xs text-gray-400 dark:text-indices-muted">
                      ({daySessions.length} sessions)
                    </span>
                  </div>

                  {daySessions.length > 0 ? (
                    <div className={`space-y-2 pl-4 border-l-2 ${isToday(day) ? 'border-primary-300 dark:border-lume-teal/50' : 'border-gray-200 dark:border-dial-border'}`}>
                      {daySessions.map(session => (
                        <SessionCard
                          key={session.id}
                          session={session}
                          onSelect={setSelectedSession}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className={`pl-4 border-l-2 ${isToday(day) ? 'border-primary-300 dark:border-lume-teal/50' : 'border-gray-200 dark:border-dial-border'}`}>
                      <p className="text-sm text-gray-400 dark:text-indices-muted py-2">No sessions</p>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Floating Action Button */}
      <FloatingButton onClick={() => setShowNewModal(true)}>
        <Icon name="plus" className="w-6 h-6" />
      </FloatingButton>

      {/* New Session Modal */}
      <NewSessionModal
        isOpen={showNewModal}
        onClose={() => setShowNewModal(false)}
        onSave={(data) => createMutation.mutate(data)}
        clientsList={clientsList}
      />

      {/* Session Detail Modal */}
      <SessionDetailModal
        session={selectedSession}
        onClose={() => setSelectedSession(null)}
        onCancel={(session) => {
          const reason = window.prompt('Cancellation reason (optional):')
          cancelMutation.mutate({ id: session.id, reason })
        }}
        onComplete={(session) => {
          completeMutation.mutate({ id: session.id })
        }}
        onNoShow={(session) => {
          noShowMutation.mutate({ id: session.id })
        }}
        onCancelSeries={(session) => {
          if (window.confirm('Cancel all future sessions in this recurring series?')) {
            cancelSeriesMutation.mutate({ id: session.id })
          }
        }}
      />
    </PageLayout>
  )
}
